import classnames from 'classnames'
import NextImage from 'components/NextImage/NextImage'
import autostart from 'components/UrPlayer/hooks/autostart'
import getPreloadMode from 'components/UrPlayer/hooks/getPreloadMode'
import startAtSavedPosition from 'components/UrPlayer/hooks/startAtSavedPosition'
import useFullscreen from 'components/UrPlayer/hooks/useFullscreen'
import captionEventHandler from 'components/UrPlayer/plugins/CaptionEventHandler/CaptionEventHandler'
import gtmEventHandler from 'components/UrPlayer/plugins/GTMEventHandler/GTMEventHandler'
import mmsNielsen from 'components/UrPlayer/plugins/MMSNielsen/MMSNielsen'
import nextEpisodeOverlay, {
  AUTOSTART_PARAM,
} from 'components/UrPlayer/plugins/NextEpisodeOverlay/nextEpisodeOverlayPlugin'
import peachEventHandler from 'components/UrPlayer/plugins/PeachEventHandler/PeachEventHandler'
import parentalLock from 'components/UrPlayer/plugins/PlayerNotifications/Notifications/parentalLock'
import PlayerNotifications from 'components/UrPlayer/plugins/PlayerNotifications/PlayerNotifications'
import ProgramInfoOverlay from 'components/UrPlayer/plugins/ProgramInfoOverlay/ProgramInfoOverlay'
import logoOverlay from 'components/UrPlayer/plugins/logoOverlay/logoOverlay'
import playerProgressBar from 'components/UrPlayer/plugins/progressTracker/PlayerProgressBar/PlayerProgressBar'
import progressTracker from 'components/UrPlayer/plugins/progressTracker/progressTracker'
import { UrPlayerConfig } from 'components/UrPlayer/types'
import UrPlayerManager, {
  FAILED_TO_FETCH_MEDIA_STREAM,
  GEOLOCATION_ERROR,
} from 'components/UrPlayer/urPlayerManager'
import {
  PLAYER_CHAPTER_BUTTON_ID,
  toggleChapterButtonAttributes,
} from 'components/UrPlayer/utils/urPlayerUtils'
import { Program, ProgramV2, Trailer } from 'frontend/types'
import { isNotAccessible, logError } from 'frontend/utils/helpers'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import styles from './Player.module.css'
import Chapters, { Chapter } from './components/Chapters/Chapters'
import NotificationErrors from './components/NotificationErrors'
import { ProductType } from 'backend/services/types/urApiService.enums'

const KeyboardControls = dynamic(
  () => import('components/UrPlayer/plugins/KeyboardControls/KeyboardControls'),
  { ssr: false },
)

const NextEpisodeOverlay = dynamic(
  () =>
    import('components/UrPlayer/plugins/NextEpisodeOverlay/NextEpisodeOverlay'),
  { ssr: false },
)

export const PLAYER_PLACEHOLDER_ID = 'player-placeholder'

interface PlayerProps {
  currentProduct: Program | ProgramV2 | Trailer
  isAudio: boolean
  accessibleEpisodes?: ProgramV2[]
  isEmbedded?: boolean
  chromecastReceiverId?: string
  includeChapters?: boolean
  player: jwplayer.JWPlayer | null
  setPlayer: (player: jwplayer.JWPlayer) => void
  setShowPlayButton?: (showPlayButton: boolean) => void
}

const JS_SRC_PATH = '/jwplayer8/jwplayer.js'
const JW_PLAYER_KEY = 'aQDWQBLCBdP+sxBg7iq9d/4QbvXOxc7VC0K8Ilofzo2tiPf5'

const Player = ({
  player,
  setPlayer,
  currentProduct,
  isAudio,
  accessibleEpisodes = [],
  isEmbedded = false,
  chromecastReceiverId,
  includeChapters,
  setShowPlayButton = (_showPlayButton: boolean) => {},
}: PlayerProps) => {
  useEffect(() => {
    window.document.documentElement.lang = 'sv'
  })

  const [showNextEpisodeOverlay, setShowNextEpisodeOverlay] = useState(false)
  const [displayGeolocationError, setDisplayGeolocationError] = useState(false)
  const [displayMediaStreamError, setDisplayMediaStreamError] = useState(false)
  const [displayUnknownPlayerError, setDisplayUnknownPlayerError] =
    useState(false)
  const [programIsNotAccessible, setProgramIsNotAccessible] = useState(
    isNotAccessible(
      currentProduct.platforms,
      currentProduct.accessiblePlatforms,
      isEmbedded,
    ),
  )
  const [showPlaceholderImage, setShowPlaceholderImage] = useState(true)

  const [chapters, setChapters] = useState<Chapter[]>([])
  const [isChaptersOpen, setIsChaptersOpen] = useState(false)

  const setPlayerPlugin = (jwPlayer: jwplayer.JWPlayer) => setPlayer(jwPlayer)
  const { isFullscreen, container: fullscreenContainer } = useFullscreen(player)

  const isReverseOrdered = currentProduct.series?.isCurrentAffairsProgram

  const sortedAccessibleEpisodes = isReverseOrdered
    ? [...accessibleEpisodes].reverse()
    : accessibleEpisodes

  const clearPlayerContainer = () => {
    const playerContainer = document.getElementById(PLAYER_PLACEHOLDER_ID)

    if (playerContainer) {
      playerContainer.className = ''
      playerContainer.innerHTML = ''
    }
  }

  useEffect(() => {
    setDisplayGeolocationError(false)
    setDisplayMediaStreamError(false)
    setDisplayUnknownPlayerError(false)

    setChapters([])
    setIsChaptersOpen(false)

    const notAccessible = isNotAccessible(
      currentProduct.platforms,
      currentProduct.accessiblePlatforms,
      isEmbedded,
    )

    setProgramIsNotAccessible(notAccessible)

    if (notAccessible) {
      setShowPlayButton(false)
      if (player) clearPlayerContainer()
    } else {
      setShowPlayButton(true)
      const manager = UrPlayerManager.getInstance(JS_SRC_PATH, JW_PLAYER_KEY)

      const autoplay: boolean = window.location.search.includes(AUTOSTART_PARAM)

      const urPlayerConfig: UrPlayerConfig = {
        onlyInSweden: currentProduct.onlyInSweden,
        urAssetId: currentProduct.id,
        format: currentProduct.format,
        programTitle: currentProduct.title,
        imageUrl:
          currentProduct.format === 'video'
            ? currentProduct.image['640x360']
            : currentProduct.image['1280x720'],
        isAudio,
        chromecastReceiverId,
        ...('chapters' in currentProduct && {
          chapters: currentProduct.chapters,
        }),
        includeChapters,
        chapterCallBack: chapterData => {
          setChapters(chapterData)
          setIsChaptersOpen(isOpen => !isOpen)
        },
      }

      const preSetupHooks = [autostart, getPreloadMode, startAtSavedPosition]

      const plugins = [
        peachEventHandler(),
        gtmEventHandler(currentProduct, autoplay, data =>
          window.dataLayer?.push(data),
        ),
        captionEventHandler,
        logoOverlay,
        parentalLock(currentProduct.parentalLock || false),
        progressTracker(sortedAccessibleEpisodes),
        setPlayerPlugin,
        mmsNielsen({
          duration: currentProduct.duration,
          format: currentProduct.format,
          id: currentProduct.id,
          seriesTitle: currentProduct.series?.title,
          title: currentProduct.title,
        }),
        playerProgressBar(currentProduct.id),
      ]
      if (currentProduct.productType !== ProductType.TRAILER) {
        plugins.push(
          nextEpisodeOverlay(
            setShowNextEpisodeOverlay,
            sortedAccessibleEpisodes,
            currentProduct.isSingle || false,
            currentProduct.id,
          ),
        )
      }

      manager
        .createPlayer(
          PLAYER_PLACEHOLDER_ID,
          urPlayerConfig,
          preSetupHooks,
          plugins,
        )
        .catch(error => {
          setShowPlayButton(false)
          if (error.message === GEOLOCATION_ERROR) {
            setDisplayGeolocationError(true)
          } else if (error.message === FAILED_TO_FETCH_MEDIA_STREAM) {
            setDisplayMediaStreamError(true)
          } else {
            setDisplayUnknownPlayerError(true)
            logError(error)
          }
          clearPlayerContainer()
        })
        .finally(() => {
          setShowPlaceholderImage(true)
          setShowNextEpisodeOverlay(false)
        })
    }
  }, [currentProduct])

  useEffect(() => {
    if (player) {
      player?.on('play', () => {
        setShowPlaceholderImage(false)
      })
      player?.on('complete', () => setShowPlaceholderImage(true))
    }
  }, [player])

  const shouldShowChapters =
    includeChapters &&
    'chapters' in currentProduct &&
    Boolean(currentProduct.chapters) &&
    fullscreenContainer

  return (
    <>
      {!programIsNotAccessible && <KeyboardControls />}
      <>
        <div
          className={classnames(styles.playerContainer, { isAudio: isAudio })}
        >
          <div className={styles.placeholderImageFrame}>
            {!isEmbedded && (
              <div
                data-testid="player-placeholder-image"
                className={classnames(styles.placeholderImage, {
                  [`${styles.placeHolderDisplay}`]: showPlaceholderImage,
                })}
              >
                <NextImage
                  src={currentProduct.image['960x540']}
                  width={960}
                  height={540}
                  alt="Programbild"
                  priority
                  onClick={() => player?.play()}
                  sizes="(min-width: 1600px) 1216px, (min-width: 1201px) calc(100vw - 16vw), 100vw"
                />
              </div>
            )}

            <div id={PLAYER_PLACEHOLDER_ID} />

            <NextEpisodeOverlay
              accessibleEpisodes={sortedAccessibleEpisodes}
              currentProgramId={currentProduct.id}
              player={player}
              showNextEpisodeOverlay={showNextEpisodeOverlay}
              setShowNextEpisodeOverlay={setShowNextEpisodeOverlay}
              isFullscreen={isFullscreen}
              fullscreenContainer={fullscreenContainer}
            />

            <ProgramInfoOverlay
              title={currentProduct.title}
              episodeNumber={currentProduct.episodeNumber || null}
              seriesTitle={currentProduct?.seriesTitle}
              mainGenre={currentProduct.mainGenre}
              seriesLabel={currentProduct.series?.label || ''}
              player={player}
              isFullscreen={isFullscreen}
              fullscreenContainer={fullscreenContainer}
            />

            <NotificationErrors
              accessiblePlatforms={currentProduct.accessiblePlatforms}
              image={currentProduct.image}
              platforms={currentProduct.platforms || []}
              programIsNotAccessible={programIsNotAccessible}
              displayMediaStreamError={
                displayMediaStreamError || displayUnknownPlayerError
              }
              displayGeolocationError={displayGeolocationError}
            />

            <PlayerNotifications
              currentProductParentalLock={currentProduct.parentalLock}
            />
          </div>
        </div>
        <div className={styles.chaptersWrapper}>
          {shouldShowChapters && (
            <Chapters
              player={player}
              isFullScreen={isFullscreen}
              fullscreenContainer={fullscreenContainer}
              chapters={chapters}
              isOpened={isChaptersOpen}
              closeButtonCallback={() => {
                setIsChaptersOpen(false)
                toggleChapterButtonAttributes()
                document
                  .querySelector<HTMLButtonElement>(
                    `div[button="${PLAYER_CHAPTER_BUTTON_ID}"]`,
                  )
                  ?.focus()
              }}
              currentProgram={currentProduct}
            />
          )}
        </div>
      </>
    </>
  )
}

export default Player
